import dayjs from 'dayjs';

/**
 * Period shortcuts, grouped the way people actually ask for a period: a rolling window, a calendar
 * period, or the financial year. The From/To pickers sit beside them and stay editable, so any range
 * that is not on this list is still one click away — a preset is a shortcut, never the only way in.
 *
 * The Indian financial year runs April–March, which is why "This FY" is not just "this year".
 */
const d = (x: dayjs.Dayjs) => x.format('YYYY-MM-DD');

export const fyStart = (offsetYears = 0) => {
  const now = dayjs().add(offsetYears, 'year');
  return now.month() >= 3 ? now.month(3).startOf('month') : now.subtract(1, 'year').month(3).startOf('month');
};

export interface Preset { label: string; group: string; range: () => [string, string] }

export const PRESET_GROUPS = ['Rolling', 'Calendar', 'Financial year'] as const;

export const PRESETS: Preset[] = [
  { label: 'Today', group: 'Rolling', range: () => [d(dayjs()), d(dayjs())] },
  { label: 'Yesterday', group: 'Rolling', range: () => [d(dayjs().subtract(1, 'day')), d(dayjs().subtract(1, 'day'))] },
  { label: 'Last 7 days', group: 'Rolling', range: () => [d(dayjs().subtract(6, 'day')), d(dayjs())] },
  { label: 'Last 30 days', group: 'Rolling', range: () => [d(dayjs().subtract(29, 'day')), d(dayjs())] },
  { label: 'Last 90 days', group: 'Rolling', range: () => [d(dayjs().subtract(89, 'day')), d(dayjs())] },

  // Weeks start on MONDAY, as they do everywhere else in the app (dayjs's default is Sunday).
  { label: 'This week', group: 'Calendar', range: () => [d(monday(dayjs())), d(dayjs())] },
  { label: 'Last week', group: 'Calendar', range: () => [d(monday(dayjs()).subtract(7, 'day')), d(monday(dayjs()).subtract(1, 'day'))] },
  { label: 'This month', group: 'Calendar', range: () => [d(dayjs().startOf('month')), d(dayjs())] },
  { label: 'Last month', group: 'Calendar', range: () => [d(dayjs().subtract(1, 'month').startOf('month')), d(dayjs().subtract(1, 'month').endOf('month'))] },
  { label: 'This quarter', group: 'Calendar', range: () => [d(dayjs().startOf('month').subtract(dayjs().month() % 3, 'month')), d(dayjs())] },
  { label: 'Last quarter', group: 'Calendar', range: () => {
    const qStart = dayjs().startOf('month').subtract(dayjs().month() % 3, 'month');
    return [d(qStart.subtract(3, 'month')), d(qStart.subtract(1, 'day'))];
  } },
  { label: 'This year', group: 'Calendar', range: () => [d(dayjs().startOf('year')), d(dayjs())] },

  { label: 'This FY', group: 'Financial year', range: () => [d(fyStart()), d(dayjs())] },
  { label: 'Last FY', group: 'Financial year', range: () => [d(fyStart(-1)), d(fyStart().subtract(1, 'day'))] },
  // Both ends open: everything on record. A real question ("everything this retailer ever bought").
  { label: 'All time', group: 'Rolling', range: () => ['', ''] },
];

/**
 * A period in words. An empty end is an OPEN end — "All time", "Up to 28 Sep 2026", "From 01 Apr
 * 2026" — never an Invalid Date. The start carries its own year only when it differs from the end's.
 */
export function describePeriod(from: string, to: string): string {
  if (!from && !to) return 'All time';
  if (!from) return `Up to ${dayjs(to).format('DD MMM YYYY')}`;
  if (!to) return `From ${dayjs(from).format('DD MMM YYYY')}`;
  return `${dayjs(from).format(dayjs(from).year() === dayjs(to).year() ? 'DD MMM' : 'DD MMM YYYY')} – ${dayjs(to).format('DD MMM YYYY')}`;
}

function monday(x: dayjs.Dayjs) { return x.subtract((x.day() + 6) % 7, 'day').startOf('day'); }

/** The period a report opens on when the reader has not picked one — its own `defaultPeriod`. */
export function defaultRangeFor(r?: { defaultPeriod?: string; periodAhead?: boolean }): [string, string] {
  const today = d(dayjs());
  if (r?.periodAhead) return [today, d(dayjs().add(180, 'day'))];
  switch (r?.defaultPeriod) {
    case 'fy': return [d(fyStart()), today];
    case 'mtd': return [d(dayjs().startOf('month')), today];
    case 'all': return ['', ''];
    default: return [d(dayjs().subtract(29, 'day')), today];
  }
}

/** Which shortcut a range IS, so a control can say "Last 30 days" instead of two dates to compare. */
export const presetFor = (from: string, to: string): string | undefined =>
  PRESETS.find((p) => { const [pf, pt] = p.range(); return pf === from && pt === to; })?.label;
